import { useEffect, useState } from 'react';
import CreateIcon from '@mui/icons-material/Create';
import SaveIcon from '@mui/icons-material/Save';
import { format } from 'date-fns';
import { useToastContext } from '@/contexts/toastContext';
import { ChangeReason, Compensation } from '@/services/compensation';
import Checkbox from '@/trio-ui/Checkbox';
import DatePicker from '@/trio-ui/DatePicker';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import { Td, Tr } from '@/trio-ui/Table';
import { calcHourlyRateFromMonthlyPayment, changeReasonOptions } from '@/utils/compensation';
import { makeDateWithCurrentTime } from '@/utils/dates';
import IconButton from '../IconButton';

export type FormData = {
  startDate: Date;
  endDate?: Date;
  hourlyRate?: string;
  monthlyPayment?: string;
  changeReason: ChangeReason;
};

export type Props = {
  isEditing?: boolean;
  onSave?: (data: FormData) => void;
  onEditClick?: () => void;
  compensation?: Compensation;
  isLoading?: boolean;
};

const StaffMemberCompensationRow = ({
  isEditing = false,
  onSave,
  onEditClick,
  compensation,
  isLoading = false,
}: Props): JSX.Element => {
  const [isCurrent, setIsCurrent] = useState<boolean>(Boolean(compensation?.endDate));
  const [hourlyRate, setHourlyRate] = useState<string | undefined>(compensation?.hourlyRate || '');
  const [monthlyPayment, setMonthlyPayment] = useState<string | undefined>(
    compensation?.monthlyPayment || ''
  );
  const [startDate, setStartDate] = useState<Date | undefined>(
    compensation ? makeDateWithCurrentTime(compensation.startDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    compensation?.endDate ? makeDateWithCurrentTime(compensation?.endDate) : undefined
  );
  const [changeReason, setChangeReason] = useState<ChangeReason | undefined>(
    compensation?.changeReason || undefined
  );
  const toast = useToastContext();

  useEffect(() => {
    const newHourlyRate = calcHourlyRateFromMonthlyPayment(monthlyPayment || '');
    setHourlyRate(newHourlyRate);
  }, [monthlyPayment]);

  useEffect(() => {
    const normalizedCompensationEndDate = compensation?.endDate
      ? new Date(compensation?.endDate)
      : new Date();
    const newEndDate = isCurrent ? undefined : normalizedCompensationEndDate;
    setEndDate(newEndDate);
  }, [isCurrent, compensation]);

  const handleSubmit = (): void => {
    if (!startDate) {
      toast.showErrorFeedback('Start date required.');
      return;
    }

    if (!changeReason) {
      toast.showErrorFeedback('Change reason required.');
      return;
    }

    if (!monthlyPayment) {
      toast.showErrorFeedback('Monthly Payment is required.');
      return;
    }

    onSave?.({
      monthlyPayment,
      hourlyRate,
      startDate,
      endDate,
      changeReason,
    });
  };

  const formatDollarValues = (value: string | undefined): string => (value ? `$ ${value}` : '--');

  if (isEditing) {
    return (
      <Tr>
        <Td className="!tw-p-1">
          <DatePicker
            className="tw-pt-5"
            name="startDate"
            value={startDate}
            onChange={setStartDate}
          />
          <Checkbox
            className="!tw-pt-1"
            label="Current compensation"
            name="isCurrent"
            checked={isCurrent}
            onChange={(e) => setIsCurrent(e.target.checked)}
          />
        </Td>
        <Td className="!tw-p-1">
          <DatePicker name="endDate" value={endDate} onChange={setEndDate} disabled={isCurrent} />
        </Td>
        <Td className="!tw-p-1 !tw-text-center">
          <span>{formatDollarValues(hourlyRate)}</span>
        </Td>
        <Td className="!tw-p-1">
          <InputText
            isLabelAboveField
            name="monthlyPayment"
            type="number"
            value={monthlyPayment}
            onChange={(e) => setMonthlyPayment(e.target.value)}
          />
        </Td>
        <Td className="!tw-p-1">
          <DropDown
            isLabelAboveField
            options={changeReasonOptions}
            name="changeReason"
            hideErrorMessage
            defaultValue={changeReason}
            onChangeDropDown={(e) => setChangeReason(e.target.value)}
          />
        </Td>
        <Td>
          <IconButton
            disabled={isLoading}
            variantStyle="primary"
            onClick={handleSubmit}
            icon={SaveIcon}
          />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      <Td className="!tw-py-[10.75px]">
        {compensation
          ? format(makeDateWithCurrentTime(compensation.startDate), 'MM/dd/yyyy')
          : '--'}
      </Td>
      <Td className="!tw-py-[10.75px]">
        {compensation?.endDate
          ? format(makeDateWithCurrentTime(compensation.endDate), 'MM/dd/yyyy')
          : 'Current'}
      </Td>
      <Td className="!tw-py-[10.75px]">{formatDollarValues(compensation?.hourlyRate)}</Td>
      <Td className="!tw-py-[10.75px]">{formatDollarValues(compensation?.monthlyPayment)}</Td>
      <Td className="!tw-py-[10.75px]">{compensation?.changeReason || '--'}</Td>
      <Td className="!tw-py-[10.75px]">
        <IconButton onClick={onEditClick} icon={CreateIcon} />
      </Td>
    </Tr>
  );
};

export default StaffMemberCompensationRow;
